import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import StayCreative from "./../../../assets/img/giftshop/landing/giphy.gif";
import type { RootState } from "../../../redux/store";
import { generateHobbyCharactersBatch } from "../../../services/caricatureHobbyService";
import { resolveHobbyCaricatureRouteSlug } from "../../../services/stylePromptResolver";
import {
  persistHobbyGenerationResult,
  type HobbyGenerationResult,
} from "../../../utils/hobbyGenerationNavigation";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import { resolveStationThemeBackground } from "../../../utils/stationTheme";
import { HOBBY_TRANSFORMATION_CUSTOM_LINE_KEY } from "../hobby-write-line";
import { setHobbyPersonCompletedResults } from "../../../redux/slices/uploadFlowSlice";


const CreatingPersonHobby = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const generationStartedRef = useRef(false);
  const mountedRef = useRef(true);
  const {
    hobbyPersonImages,
    hobbyPersonHobbySelections,
    hobbyPeopleCount,
    hobbyPersonCompletedResults,
    hobbyGroupPhotoFlow,
  } = useSelector((s: RootState) => s.uploadFlow);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);


  const stationData = useMemo(() => {
    const fromNav = (location.state as { stationData?: Record<string, unknown> } | null)
      ?.stationData;
    if (fromNav && typeof fromNav === "object" && fromNav.type) {
      return fromNav as Record<string, unknown> & { type?: string; name?: string };
    }
    try {
      const selectedRaw = localStorage.getItem("selectedStation");
      const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
      const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
      return fromSelected || fromStorage;
    } catch {
      return {};
    }
  }, [location.state]);

  const themeBackground = useMemo(
    () => resolveStationThemeBackground(stationData as any),
    [stationData]
  );

  const routeSlug = useMemo(
    () => resolveHobbyCaricatureRouteSlug(slug),
    [slug]
  );

  const currentPersonIndex = useMemo(() => {
    const fromState = (location.state as { currentPersonIndex?: number } | null)?.currentPersonIndex;
    if (typeof fromState === "number" && fromState >= 0) {
      return fromState;
    }
    return Math.max(0, hobbyPersonCompletedResults.length);
  }, [location.state, hobbyPersonCompletedResults.length]);

  const currentPersonNumber = currentPersonIndex + 1;
  const totalPeople = hobbyPeopleCount ?? 1;
  const batchGroupGeneration = Boolean(
    (location.state as { batchGroupGeneration?: boolean } | null)?.batchGroupGeneration ||
      (hobbyGroupPhotoFlow &&
        totalPeople > 1 &&
        hobbyPersonHobbySelections.filter((item) => item?.id).length >= totalPeople &&
        hobbyPersonCompletedResults.length === 0)
  );
  const retryGeneration = Boolean(
    (location.state as { retryGeneration?: boolean } | null)?.retryGeneration
  );
  const previousGenerationResult = (location.state as {
    previousGenerationResult?: HobbyGenerationResult;
  } | null)?.previousGenerationResult;

  // Drive the progress bar while generating. Kept separate from the generation
  // effect so re-renders never restart or duplicate the (paid) AI request.
  useEffect(() => {
    if (!isGenerating) return;
    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev >= 95 ? prev : prev + 1));
    }, 120);
    return () => clearInterval(progressInterval);
  }, [isGenerating]);

  // Run generation exactly once per mount (and once per explicit retry). The
  // ref guard prevents history-back/remounts from firing another AI request.
  useEffect(() => {
    if (generationStartedRef.current) return;
    generationStartedRef.current = true;

    const run = async () => {
      setIsGenerating(true);
      setError(null);
      setProgress(0);

      const stateHobbies =
        ((location.state as { selectedHobbies?: typeof hobbyPersonHobbySelections } | null)
          ?.selectedHobbies ||
          hobbyPersonHobbySelections) ?? [];

      let hobbyNames: string[] = [];
      let selfieSources: string[] = [];

      if (batchGroupGeneration) {
        // One batch request: every extracted person + their selected hobby prompt.
        const count = Math.min(
          totalPeople,
          stateHobbies.length,
          hobbyPersonImages.length
        );
        hobbyNames = stateHobbies
          .slice(0, count)
          .map((item) => item?.name)
          .filter((name): name is string => Boolean(name?.trim()));
        selfieSources = hobbyPersonImages
          .slice(0, hobbyNames.length)
          .filter((url): url is string => Boolean(url?.trim()));
      } else {
        const currentHobby =
          hobbyPersonHobbySelections[currentPersonIndex] || stateHobbies[currentPersonIndex];
        const currentSelfie = hobbyPersonImages[currentPersonIndex];
        hobbyNames = currentHobby?.name ? [currentHobby.name] : [];
        selfieSources = currentSelfie ? [currentSelfie] : [];
      }

      const customLineFromState =
        typeof (location.state as { customLine?: string } | null)?.customLine === "string"
          ? (location.state as { customLine?: string }).customLine!.trim()
          : "";
      let customLine = customLineFromState;
      if (!customLine) {
        try {
          customLine = (localStorage.getItem(HOBBY_TRANSFORMATION_CUSTOM_LINE_KEY) || "").trim();
        } catch {
          customLine = "";
        }
      }
      customLine = customLine.slice(0, 45);

      const result = await generateHobbyCharactersBatch({
        hobbyNames,
        selfieSources,
        customLine: customLine || undefined,
      });

      if (!mountedRef.current) return;

      if (!result.success) {
        setIsGenerating(false);
        setProgress(0);
        setError(result.error || "Failed to generate hobby caricatures.");
        return;
      }

      setProgress(100);
      setIsGenerating(false);
      const generationState: HobbyGenerationResult = {
        imageUrls: result.imageUrls,
        imageUrlsNoLogo: result.imageUrlsNoLogo,
        raw: result.data,
      };
      persistHobbyGenerationResult(generationState);

      if (batchGroupGeneration) {
        dispatch(setHobbyPersonCompletedResults(result.imageUrls));
        navigate(`/${routeSlug}/created-person-hobby`, {
          replace: true,
          state: {
            generationResult: generationState,
            stationData,
            isFinalCombine: true,
          },
        });
        return;
      }

      if (retryGeneration && previousGenerationResult?.imageUrls?.length) {
        navigate(`/${routeSlug}/hobby-result-compare`, {
          replace: true,
          state: {
            resultOne: previousGenerationResult,
            resultTwo: generationState,
            stationData,
            currentPersonIndex,
          },
        });
        return;
      }

      navigate(`/${routeSlug}/hobby-result-review`, {
        replace: true,
        state: {
          generationResult: generationState,
          stationData,
          currentPersonIndex,
        },
      });
    };

    run();
    // Generation should fire once per mount / retry only; other values are read
    // at run time and intentionally excluded to avoid re-triggering the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey]);

  return (
    <StationThemeLayout
      className="personHobbySection personHobbySection--creating p-0"
      station={stationData as any}
      showStationLogo
      showBack={false}
    >
      <div className="selectBrand">
        <div className="creatingPersonHobbyContent">
          <div className="heading">
            {batchGroupGeneration
              ? `Creating ${totalPeople} Caricature Hobbies`
              : `Creating Person ${currentPersonNumber}${
                  totalPeople > 1 ? ` of ${totalPeople}` : ""
                } Caricature Hobby`}
          </div>
          <div className="description px-5 my-4" style={{ fontSize: "20px", color: "#fff" }}>
            {batchGroupGeneration
              ? "We are creating cartoon versions for everyone from your group photo. Just a few seconds!"
              : "We are creating a cartoon version for this person based on their selected hobby. Just a few seconds!"}
          </div>
          <div
            className="hobbyImgBlendWrap mt-4"
          >
            <img
              src={StayCreative}
              alt="Creating caricature animation"
              className="hobbyImg"
            />
          </div>

          <div className="creatingPersonHobbyProgress my-5">
            <div className="progressBarContainer">
              <div
                className="progressBar"
                role="progressbar"
                style={{
                  width: `${Math.max(progress, progress > 0 ? 4 : 0)}%`,
                  transition: "width 0.1s linear",
                }}
                aria-valuenow={Math.round(progress)}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
          {error && (
            <div className="small text-danger mt-2" role="alert">
              {error}
            </div>
          )}
          {!isGenerating && (
            <button
              className="proceedBTN mt-3"
              onClick={() => {
                generationStartedRef.current = false;
                setRetryKey((v) => v + 1);
              }}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </StationThemeLayout>
  );
};

export default CreatingPersonHobby;
